import { useEffect, useRef } from "react";

interface UseListenPasteProps {
  textLength: number;
  isNumber?: boolean;
  onPaste?: (text: string) => void;
}

const useListenPaste = ({ textLength, onPaste, isNumber = true }: UseListenPasteProps): void => {
  // Keep a ref of the expected length so callbacks always see the latest value
  const lengthRef = useRef<number>(textLength);
  useEffect(() => {
    lengthRef.current = textLength;
  }, [textLength]);

  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      e.preventDefault();
      let pastedText = "";

      try {
        // Primary: navigator.clipboard API
        if (navigator.clipboard?.readText) {
          pastedText = (await navigator.clipboard.readText()).trim();
        }
        // Fallback: older browsers
        else if (e.clipboardData) {
          pastedText = e.clipboardData.getData("text/plain").trim();
        }

        if (pastedText.length === lengthRef.current && (!isNumber || !isNaN(Number(pastedText)))) {
          onPaste?.(pastedText);
        }
      } catch (err) {
        console.warn("Primary paste failed:", err);

        // Fallback: try clipboardData again
        if (e.clipboardData) {
          const fallback = e.clipboardData.getData("text/plain").trim();
          if (fallback.length === lengthRef.current && (!isNumber || !isNaN(Number(fallback)))) {
            onPaste?.(fallback);
          }
        }
      }
    };

    const handleBeforeInput = (e: InputEvent) => {
      // Detect "insertFromPaste" on modern mobile browsers
      if (e.inputType === "insertFromPaste") {
        const target = e.target as HTMLInputElement | HTMLTextAreaElement;
        setTimeout(() => {
          const val = target.value.trim();
          if (val.length === lengthRef.current && (!isNumber || !isNaN(Number(val)))) {
            onPaste?.(val);
          }
        }, 0);
      }
    };

    const handleInput = (e: Event) => {
      // Generic fallback for value changes
      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
      const val = target.value.trim();
      if (val.length === lengthRef.current && (!isNumber || !isNaN(Number(val)))) {
        onPaste?.(val);
      }
    };

    document.addEventListener("paste", handlePaste);
    document.addEventListener("beforeinput", handleBeforeInput);
    document.addEventListener("input", handleInput);

    return () => {
      document.removeEventListener("paste", handlePaste);
      document.removeEventListener("beforeinput", handleBeforeInput);
      document.removeEventListener("input", handleInput);
    };
  }, [onPaste, isNumber]);
};

export default useListenPaste;
